// Notification bell — lives in the AppShell top bar next to the
// balance pill. Gold unread-count badge; Popover lists the 20 newest
// notifications; clicking one marks it read and deep-links to
// wherever it came from (link_path). "Mark all read" clears the badge.

import { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@greenhold/components";

import { useMarkNotificationsRead } from "./useMarkNotificationsRead";
import { useNotifications, type Notification } from "./useNotifications";

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const deltaSecs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (deltaSecs < 60) return "just now";
  if (deltaSecs < 3600) return `${Math.floor(deltaSecs / 60)}m ago`;
  if (deltaSecs < 86400) return `${Math.floor(deltaSecs / 3600)}h ago`;
  return `${Math.floor(deltaSecs / 86400)}d ago`;
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const query = useNotifications();
  const markRead = useMarkNotificationsRead();

  // Array.isArray guard: an unexpected envelope (proxy error page, test
  // stub) must degrade to an empty bell, not crash the whole shell.
  const items = Array.isArray(query.data?.data) ? query.data.data : [];
  const unread =
    typeof query.data?.unread_count === "number" ? query.data.unread_count : 0;
  const badgeText = unread > 99 ? "99+" : String(unread);

  const handleItemClick = (n: Notification) => {
    if (!n.read_at) {
      markRead.mutate({ ids: [n.id] });
    }
    setOpen(false);
    if (n.link_path) {
      navigate(n.link_path);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="relative rounded p-1.5 text-brand-paper-100/80 transition-colors hover:bg-brand-forest-800/40 hover:text-brand-paper-100"
          aria-label={
            unread > 0
              ? `Notifications (${unread} unread)`
              : "Notifications"
          }
          data-testid="shell-notification-bell"
        >
          {/* Bell glyph — inline SVG so no icon-library dependency. */}
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-5 w-5"
          >
            <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
          {unread > 0 && (
            <span
              className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-gold-500 px-1 font-body text-[10px] font-bold leading-none text-brand-forest-900"
              data-testid="shell-notification-count"
            >
              {badgeText}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 border-brand-gold-500/20 bg-brand-forest-900 p-0 text-brand-paper-100"
      >
        <div className="flex items-center justify-between border-b border-brand-gold-500/20 px-4 py-2.5">
          <p className="font-heading text-sm font-bold">Notifications</p>
          {unread > 0 && (
            <button
              type="button"
              className="font-body text-xs text-brand-gold-300 hover:brightness-110"
              onClick={() => markRead.mutate({})}
            >
              Mark all read
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {query.isPending ? (
            <p className="px-4 py-6 text-center font-body text-sm opacity-60">
              Loading…
            </p>
          ) : items.length === 0 ? (
            <p className="px-4 py-6 text-center font-body text-sm opacity-60">
              Nothing yet — approvals, sales, and roster news land here.
            </p>
          ) : (
            <ul>
              {items.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => handleItemClick(n)}
                    className={[
                      "w-full border-b border-brand-gold-500/10 px-4 py-3 text-left transition-colors hover:bg-brand-forest-800/40",
                      n.read_at ? "opacity-60" : "",
                    ].join(" ")}
                  >
                    <span className="flex items-start gap-2">
                      {!n.read_at && (
                        <span
                          aria-hidden="true"
                          className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-gold-500"
                        />
                      )}
                      <span className="min-w-0">
                        <span className="block font-body text-sm font-semibold">
                          {n.title}
                        </span>
                        {n.body && (
                          <span className="mt-0.5 block font-body text-xs opacity-80">
                            {n.body}
                          </span>
                        )}
                        <span className="mt-1 block font-body text-[11px] opacity-50">
                          {relativeTime(n.created_at)}
                        </span>
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
